import React, { useState, useEffect } from 'react';
import { Check, Cpu, Zap, Network } from 'lucide-react';
import { terminalLogs } from '../data/mockData';

const SystemArchitecture = () => {
  const [currentLog, setCurrentLog] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLog((prev) => (prev + 1) % terminalLogs.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const platforms = [
    { name: 'Polymarket', color: '#f2e2a6' },
    { name: 'Kalshi', color: '#60a5fa' },
    { name: 'Opinion', color: '#f59e0b' },
    { name: 'Probable', color: '#a78bfa' },
    { name: 'Predict.fun', color: '#ec4899' }
  ];

  return (
    <section className="relative py-20 bg-gradient-to-b from-[#06070d] via-[#0b0e1a] to-[#06070d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Predictive <span className="text-[#f2e2a6]">Intelligence.</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Our hybrid model combines 5 technical indicators with informed market making. Not latency arbitrage - intelligent position taking with strict risk controls.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Predictive Model */}
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-[#f2e2a6]/50 transition-all group">
            <div className="w-12 h-12 bg-[#f2e2a6]/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#f2e2a6]/30 transition-colors">
              <Cpu className="w-6 h-6 text-[#f2e2a6]" />
            </div>
            <h3 className="text-white text-2xl font-bold mb-4">5-Signal Predictive Model</h3>
            <p className="text-gray-400 mb-6">
              Momentum (40%), Acceleration (20%), Mean Reversion (20%), Orderbook Imbalance (20%), Volume Amplifier. Generates confidence-scored BUY/SELL/NEUTRAL signals.
            </p>
            
            <div className="space-y-3">
              <div className="bg-black/40 border border-green-500/30 rounded-lg p-3">
                <div className="text-green-400 text-xs font-mono mb-1">STRONG BUY (78% conf)</div>
                <div className="text-gray-300 text-xs">Momentum: 0.85 | Accel: 0.72 | Vol: High</div>
              </div>
              <div className="bg-black/40 border border-red-500/30 rounded-lg p-3">
                <div className="text-red-400 text-xs font-mono mb-1">STRONG SELL (65% conf)</div>
                <div className="text-gray-300 text-xs">Mean Rev: 0.68 | Imbalance: -0.55</div>
              </div>
            </div>
          </div>

          {/* Risk Management */}
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-[#f2e2a6]/50 transition-all group">
            <div className="w-12 h-12 bg-[#f2e2a6]/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#f2e2a6]/30 transition-colors">
              <Zap className="w-6 h-6 text-[#f2e2a6]" />
            </div>
            <h3 className="text-white text-2xl font-bold mb-4">3-Layer Risk Management</h3>
            <p className="text-gray-400 mb-6">
              Hard limits, circuit breakers, and kill switch protect capital. Every trade has -3% stop loss and +6% take profit (2:1 R/R).
            </p>
            
            <div className="bg-black/60 border border-white/10 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Position Limit</span>
                <span className="text-white font-mono">$1,000</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Daily Loss Limit</span>
                <span className="text-red-400 font-mono">-$500</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Stop Loss</span>
                <span className="text-red-400 font-mono">-3%</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Take Profit</span>
                <span className="text-green-400 font-mono">+6%</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Max Drawdown</span>
                <span className="text-yellow-400 font-mono">12%</span>
              </div>
            </div>
          </div>

          {/* Market Intelligence */}
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-[#f2e2a6]/50 transition-all group">
            <div className="w-12 h-12 bg-[#f2e2a6]/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#f2e2a6]/30 transition-colors">
              <Network className="w-6 h-6 text-[#f2e2a6]" />
            </div>
            <h3 className="text-white text-2xl font-bold mb-4">Smart Market Selection</h3>
            <p className="text-gray-400 mb-6">
              Automatically filters out hyper-competitive markets (5m, 15m). Focuses on Daily+ markets with better opportunity/risk ratio.
            </p>
            
            <div className="space-y-3">
              <div className="bg-black/40 border border-red-500/30 rounded-lg p-3">
                <div className="text-red-400 text-xs font-bold mb-1">❌ BLACKLIST</div>
                <div className="text-gray-400 text-xs">5min, 15min, BTC/USD, ETH/USD</div>
              </div>
              <div className="bg-black/40 border border-green-500/30 rounded-lg p-3">
                <div className="text-green-400 text-xs font-bold mb-1">✅ WHITELIST</div>
                <div className="text-gray-400 text-xs">Daily, Weekly, Monthly, Events</div>
              </div>
              <div className="bg-black/40 border border-blue-500/30 rounded-lg p-3">
                <div className="text-blue-400 text-xs font-bold mb-1">📊 CRITERIA</div>
                <div className="text-gray-400 text-xs">Min Vol: $1k | Max Spread: 100bps</div>
              </div>
            </div>
          </div>
        </div>

        {/* Strategy overview - no performance claims */}
        <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white text-2xl font-bold">How It Works</h3>
            <span className="text-gray-400 text-sm uppercase tracking-wider">Informed Market Making</span>
          </div>
          <p className="text-gray-400 mb-8">
            The system generates trading signals based on technical indicators, but actual results depend on market conditions, execution, and user-configured risk parameters. Past signals do not guarantee future performance.
          </p>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/60 border border-white/10 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-[#f2e2a6] mb-2">5</div>
              <div className="text-gray-400 text-sm">Signal Indicators</div>
            </div>
            <div className="bg-black/60 border border-white/10 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-[#f2e2a6] mb-2">2:1</div>
              <div className="text-gray-400 text-sm">Risk/Reward Target</div>
            </div>
            <div className="bg-black/60 border border-white/10 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-[#f2e2a6] mb-2">3%</div>
              <div className="text-gray-400 text-sm">Stop Loss Default</div>
            </div>
            <div className="bg-black/60 border border-white/10 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">Daily+</div>
              <div className="text-gray-400 text-sm">Market Focus</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemArchitecture;
